import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Play, Square, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "../components/Format";
import LiveTradeWarningDialog from "../components/LiveTradeWarningDialog";
import BotOptimizerPanel from "../components/BotOptimizerPanel";

const STRATEGIES = [
  { value: "scalping", label: "Scalping", desc: "Capture tiny price moves repeatedly" },
  { value: "arbitrage", label: "Arbitrage", desc: "Exploit price spreads across venues" },
  { value: "market_making", label: "Market Making", desc: "Provide bid/ask liquidity" },
  { value: "indicator", label: "Indicator (RSI/SMA)", desc: "Trade on technical signals" },
];

const CRYPTO_SYMBOLS = ["BTC", "ETH", "SOL", "BNB", "XRP", "ADA", "DOGE", "LINK", "MATIC", "AVAX"];
const STOCK_SYMBOLS = ["AAPL", "TSLA", "NVDA", "MSFT", "GOOGL", "AMZN", "META", "AMD", "NFLX", "SPY"];

export default function Bots() {
  const { mode } = useAuth();
  const [bots, setBots] = useState([]);
  const [open, setOpen] = useState(false);
  const [warnBot, setWarnBot] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/bots");
      setBots(r.data);
    } catch { /* ignore transient refresh failures */ }
  }, []);
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 2000);
    return () => clearInterval(id);
  }, [refresh]);

  // If we arrived via ?spawn=1, pop the prefill from sessionStorage and open dialog
  useEffect(() => {
    if (searchParams.get("spawn") === "1") {
      try {
        const raw = sessionStorage.getItem("mt_spawn_bot");
        if (raw) {
          setPrefill(JSON.parse(raw));
          sessionStorage.removeItem("mt_spawn_bot");
          setOpen(true);
        }
      } catch { /* corrupt prefill — ignore */ }
      searchParams.delete("spawn");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  async function actuallyStart(id) {
    try {
      await api.post(`/bots/${id}/start`);
      toast.success("Bot started");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start");
    }
  }

  async function startBot(bot) {
    if (bot.mode === "live") {
      setWarnBot(bot);
      return;
    }
    actuallyStart(bot.id);
  }
  async function stopBot(id) {
    try {
      await api.post(`/bots/${id}/stop`);
      toast.success("Bot stopped");
      refresh();
    } catch { toast.error("Failed to stop bot"); }
  }
  async function deleteBot(id) {
    if (!window.confirm("Delete this bot? Its trades are kept in history.")) return;
    try {
      await api.delete(`/bots/${id}`);
      toast.success("Bot deleted");
      refresh();
    } catch { toast.error("Failed to delete bot"); }
  }

  return (
    <div className="space-y-6" data-testid="bots-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="label-xs">Strategies</div>
          <h1 className="text-3xl font-[Chivo] tracking-tight">Trading Bots</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-bot-button" className="bg-[#007AFF] hover:bg-[#3395FF]">
              <Plus size={16} className="mr-2" /> New Bot
            </Button>
          </DialogTrigger>
          <NewBotDialog
            key={prefill?.symbol || "default"}
            onCreated={() => { setOpen(false); setPrefill(null); refresh(); }}
            defaultMode={mode}
            prefill={prefill}
          />
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-px bg-border" data-testid="bots-grid">
        {bots.length === 0 && (
          <div className="col-span-full bg-[#0F0F0F] p-10 text-center text-neutral-400 border border-border">
            <Zap className="mx-auto mb-3 text-neutral-600" />
            <div className="font-[Chivo]">No bots yet</div>
            <div className="text-xs mt-1">Create your first bot to start producing micro-trades.</div>
          </div>
        )}
        {bots.map((b) => (
          <div key={b.id} className="bg-[#0F0F0F] p-5 flex flex-col gap-3" data-testid={`bot-card-${b.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-[Chivo] text-lg">{b.name}</div>
                <div className="label-xs">{b.strategy} · {b.symbol} · {b.exchange}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 border ${
                b.status === "running"
                  ? "border-[#00E676] text-[#00E676]"
                  : "border-neutral-700 text-neutral-400"
              }`}>{b.status === "running" ? "● LIVE" : "○ IDLE"}</span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <Mini k="Mode" v={b.mode.toUpperCase()} />
              <Mini k="Size" v={`$${fmtMoney(b.config?.trade_size_usd ?? 0, 0)}`} />
              <Mini k="Freq/min" v={b.config?.frequency_per_min ?? "—"} />
              <Mini k="Max Trades" v={b.config?.max_trades ?? "—"} />
            </div>

            <div className="grid grid-cols-3 gap-2 text-xs border-t border-border pt-3">
              <Mini k="Trades" v={<span className="num">{b.stats?.trades ?? 0}</span>} />
              <Mini k="Win Rate" v={
                <span className="num">{b.stats?.trades ? ((b.stats.wins / b.stats.trades) * 100).toFixed(0) : 0}%</span>
              } />
              <Mini k="PnL" v={
                <span className={`num ${(b.stats?.pnl ?? 0) >= 0 ? "profit" : "loss"}`}>
                  {(b.stats?.pnl ?? 0) >= 0 ? "+" : ""}${fmtMoney(b.stats?.pnl ?? 0, 2)}
                </span>
              } />
            </div>

            <div className="flex gap-2 mt-auto">
              {b.status === "running" ? (
                <Button
                  data-testid={`stop-bot-${b.id}`}
                  onClick={() => stopBot(b.id)}
                  variant="outline" className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 bg-transparent flex-1"
                >
                  <Square size={14} className="mr-1" /> Stop
                </Button>
              ) : (
                <Button
                  data-testid={`start-bot-${b.id}`}
                  onClick={() => startBot(b)}
                  className="bg-[#00E676] hover:bg-[#00ff7e] text-black flex-1"
                >
                  <Play size={14} className="mr-1" /> Start
                </Button>
              )}
              <Button
                data-testid={`delete-bot-${b.id}`}
                onClick={() => deleteBot(b.id)}
                variant="outline" className="border-border bg-transparent hover:bg-[#141414]"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <LiveTradeWarningDialog
        open={!!warnBot}
        onOpenChange={(o) => { if (!o) setWarnBot(null); }}
        bot={warnBot}
        onConfirm={async () => {
          const id = warnBot.id;
          setWarnBot(null);
          await actuallyStart(id);
        }}
      />
    </div>
  );
}

function Mini({ k, v }) {
  return (
    <div>
      <div className="label-xs">{k}</div>
      <div className="text-sm">{v}</div>
    </div>
  );
}

function NewBotDialog({ onCreated, defaultMode, prefill }) {
  const [name, setName] = useState(prefill?.name || "Scalper-1");
  const [strategy, setStrategy] = useState(prefill?.strategy || "scalping");
  const [assetClass, setAssetClass] = useState(prefill?.asset_class || "crypto");
  const [symbol, setSymbol] = useState(prefill?.symbol || "BTC");
  const [mode, setMode] = useState(prefill?.mode || defaultMode || "paper");
  const [exchange, setExchange] = useState(prefill?.exchange || "coinbase");
  const [tradeSize, setTradeSize] = useState(100);
  const [frequency, setFrequency] = useState(60);
  const [maxTrades, setMaxTrades] = useState(2000);
  const [stopLoss, setStopLoss] = useState(0.3);
  const [takeProfit, setTakeProfit] = useState(0.5);
  const [spread, setSpread] = useState(5);
  const [loading, setLoading] = useState(false);
  const [strategiesMeta, setStrategiesMeta] = useState([]);

  useEffect(() => {
    api.get("/strategies").then((r) => setStrategiesMeta(r.data)).catch(() => {});
  }, []);

  function applyRecommended() {
    const meta = strategiesMeta.find((m) => m.id === strategy);
    if (!meta) {
      toast.error("Strategy metadata not loaded yet");
      return;
    }
    const rec = meta.recommended;
    setTradeSize(rec.trade_size_usd);
    setFrequency(rec.frequency_per_min);
    setMaxTrades(rec.max_trades);
    setStopLoss(rec.stop_loss_pct);
    setTakeProfit(rec.take_profit_pct);
    setSpread(rec.spread_bps);
    toast.success(`Applied recommended settings for ${meta.label}`);
  }

  const symbolList = assetClass === "crypto" ? CRYPTO_SYMBOLS : STOCK_SYMBOLS;
  const exchanges = assetClass === "crypto" ? ["coinbase", "btcc"] : ["public", "webull", "sofi"];

  async function submit() {
    setLoading(true);
    try {
      await api.post("/bots", {
        name, strategy, asset_class: assetClass, symbol, mode, exchange,
        config: {
          trade_size_usd: Number(tradeSize),
          frequency_per_min: Number(frequency),
          max_trades: Number(maxTrades),
          stop_loss_pct: Number(stopLoss),
          take_profit_pct: Number(takeProfit),
          spread_bps: Number(spread),
          indicator: "rsi",
          indicator_threshold: 30,
        },
      });
      toast.success("Bot created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create bot");
    } finally {
      setLoading(false);
    }
  }

  return (
    <DialogContent className="bg-[#0F0F0F] border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-bot-dialog">
      <DialogHeader>
        <DialogTitle className="font-[Chivo] text-xl tracking-tight">Configure New Bot</DialogTitle>
      </DialogHeader>

      <BotOptimizerPanel
        config={{
          strategy,
          trade_size_usd: Number(tradeSize) || 0,
          spread_bps: Number(spread) || 0,
          stop_loss_pct: Number(stopLoss) || 0,
          take_profit_pct: Number(takeProfit) || 0,
          frequency_per_min: Number(frequency) || 0,
          max_trades: Number(maxTrades) || 0,
        }}
        onApplyRecommended={applyRecommended}
      />

      <div className="grid grid-cols-2 gap-4">
        <Field label="Name">
          <Input data-testid="bot-name-input" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0A0A0A] border-border" />
        </Field>
        <Field label="Strategy">
          <Select value={strategy} onValueChange={setStrategy}>
            <SelectTrigger data-testid="bot-strategy-select" className="bg-[#0A0A0A] border-border"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-border">
              {STRATEGIES.map((s) => (
                <SelectItem key={s.value} value={s.value} className="hover:bg-[#1a1a1a]">
                  <div>
                    <div className="font-[Chivo]">{s.label}</div>
                    <div className="text-xs text-neutral-500">{s.desc}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Asset Class">
          <Select value={assetClass} onValueChange={(v) => {
            setAssetClass(v);
            setSymbol(v === "crypto" ? "BTC" : "AAPL");
            setExchange(v === "crypto" ? "coinbase" : "public");
          }}>
            <SelectTrigger data-testid="bot-asset-class" className="bg-[#0A0A0A] border-border"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-border">
              <SelectItem value="crypto">Crypto</SelectItem>
              <SelectItem value="stock">Stock</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Symbol">
          <Select value={symbol} onValueChange={setSymbol}>
            <SelectTrigger data-testid="bot-symbol-select" className="bg-[#0A0A0A] border-border"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-border">
              {symbolList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Exchange">
          <Select value={exchange} onValueChange={setExchange}>
            <SelectTrigger data-testid="bot-exchange-select" className="bg-[#0A0A0A] border-border"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-border">
              {exchanges.map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Mode">
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="bot-mode-select" className="bg-[#0A0A0A] border-border"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-border">
              <SelectItem value="paper">Paper (simulated)</SelectItem>
              <SelectItem value="live">Live (requires API keys)</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field label="Trade Size (USD)">
          <Input data-testid="bot-trade-size" type="number" value={tradeSize} onChange={(e) => setTradeSize(e.target.value)} className="bg-[#0A0A0A] border-border" />
        </Field>
        <Field label="Trades / minute">
          <Input data-testid="bot-frequency" type="number" value={frequency} onChange={(e) => setFrequency(e.target.value)} className="bg-[#0A0A0A] border-border" />
        </Field>

        <Field label="Max Trades (session cap)">
          <Input data-testid="bot-max-trades" type="number" value={maxTrades} onChange={(e) => setMaxTrades(e.target.value)} className="bg-[#0A0A0A] border-border" />
        </Field>
        <Field label="Spread (bps)">
          <Input data-testid="bot-spread" type="number" value={spread} onChange={(e) => setSpread(e.target.value)} className="bg-[#0A0A0A] border-border" />
        </Field>

        <Field label="Stop Loss %">
          <Input data-testid="bot-sl" type="number" step="0.1" value={stopLoss} onChange={(e) => setStopLoss(e.target.value)} className="bg-[#0A0A0A] border-border" />
        </Field>
        <Field label="Take Profit %">
          <Input data-testid="bot-tp" type="number" step="0.1" value={takeProfit} onChange={(e) => setTakeProfit(e.target.value)} className="bg-[#0A0A0A] border-border" />
        </Field>
      </div>

      <DialogFooter>
        <Button onClick={submit} disabled={loading} data-testid="submit-new-bot" className="bg-[#007AFF] hover:bg-[#3395FF]">
          {loading ? "Creating…" : "Create Bot"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="label-xs">{label}</Label>
      {children}
    </div>
  );
}
